import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../../config';
// @ts-ignore
import SurveyConnectLogo from '../../assets/survey_connect_logo.png';

type GateState = 'checking' | 'allowed' | 'blocked' | 'permission_denied' | 'error';

function isInZimbabwe(lat: number, lng: number): boolean {
  const { latMin, latMax, lngMin, lngMax } = config.ZW_BOUNDS;
  return lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax;
}

export const ZimbabweGatePage: React.FC = () => {
  const [state, setState] = useState<GateState>('checking');
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo: string = (location.state as any)?.returnTo || '/login';

  const checkLocation = () => {
    setState('checking');
    if (!navigator.geolocation) {
      setState('error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (isInZimbabwe(pos.coords.latitude, pos.coords.longitude)) {
          sessionStorage.setItem('zw_gate_passed', '1');
          setState('allowed');
          navigate(returnTo, { replace: true });
        } else {
          setState('blocked');
        }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setState('permission_denied');
        else setState('error');
      },
      { timeout: 10000 }
    );
  };

  useEffect(() => { checkLocation(); }, []);

  if (state === 'checking') {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <img src={SurveyConnectLogo} alt="Survey Connect" style={styles.gateLogo} />
          <div style={styles.spinner} />
          <h2 style={styles.title}>Checking your location...</h2>
          <p style={styles.text}>Please allow location access when prompted.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src={SurveyConnectLogo} alt="Survey Connect" style={styles.gateLogo} />
        <div style={styles.icon}>🇿🇼</div>
        <h2 style={styles.title}>
          {state === 'blocked' ? 'Available in Zimbabwe Only' : state === 'permission_denied' ? 'Location Permission Required' : 'Location Error'}
        </h2>
        <p style={styles.text}>
          {state === 'blocked'
            ? "Survey Connect is currently only available within Zimbabwe. Please ensure you are located within the country."
            : state === 'permission_denied'
            ? 'We need access to your location to verify you are in Zimbabwe. Please enable location permissions in your browser.'
            : 'We were unable to determine your location. Please try again.'}
        </p>
        <button onClick={checkLocation} style={styles.retryBtn}>🔄 Retry Location Check</button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: '100vh', 
    background: 'var(--sc-bg-primary, #F5F7FA)', 
    fontFamily: 'var(--sc-font-family, sans-serif)',
    padding: 16,
  },
  card: { 
    background: 'var(--sc-bg-secondary, #FFFFFF)', 
    borderRadius: 'var(--sc-radius-xl, 20px)', 
    padding: '48px 32px', 
    width: '100%',
    maxWidth: 440,
    textAlign: 'center' as const, 
    boxShadow: 'var(--sc-shadow-xl)',
    border: '1px solid var(--sc-border-light, #F1F5F9)',
    boxSizing: 'border-box',
  },
  gateLogo: {
    width: 180,
    height: 50,
    objectFit: 'contain',
    margin: '0 auto 24px',
    display: 'block',
    filter: 'var(--sc-logo-filter)',
  },
  icon: { fontSize: 64, marginBottom: 16 },
  title: { 
    fontSize: 22, 
    fontWeight: 800, 
    color: 'var(--sc-text-primary, #0A1C30)', 
    margin: '0 0 8px',
    fontFamily: 'var(--sc-font-display, sans-serif)',
    letterSpacing: '-0.5px',
  },
  text: { 
    fontSize: 14, 
    color: 'var(--sc-text-secondary, #475569)', 
    lineHeight: '1.6', 
    margin: '0 0 28px' 
  },
  retryBtn: { 
    height: 'var(--sc-touch-btn, 48px)',
    width: '100%',
    background: 'var(--sc-brand-gradient, #0C5ADB)', 
    color: 'var(--sc-text-on-brand, #FFF)', 
    border: 'none', 
    borderRadius: 'var(--sc-radius-md, 12px)', 
    fontSize: 15, 
    fontWeight: 700, 
    cursor: 'pointer', 
    fontFamily: 'inherit',
    boxShadow: 'var(--sc-shadow-brand)',
    transition: 'all var(--sc-transition-fast)',
  },
  spinner: { 
    width: 40, 
    height: 40, 
    border: '4px solid var(--sc-border-light, #E8ECF0)', 
    borderTopColor: 'var(--sc-brand-primary, #0C5ADB)', 
    borderRadius: '50%', 
    animation: 'sc-spin 0.8s linear infinite', 
    margin: '0 auto 20px' 
  },
};
